import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Shield,
  LogOut,
  Thermometer,
  FlaskConical,
  Menu,
  Workflow,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../components/ui/sheet';
import OwnershipFooter from '../components/common/OwnershipFooter';
import permissions from '../lib/permissions';

const NAV_BASE = [
  { name: 'Dashboard', href: '/mvp', icon: LayoutDashboard, key: 'dashboard' },
  { name: 'Batches', href: '/mvp/batches', icon: Package, key: 'batches' },
  { name: 'Chain of Custody', href: '/mvp/chain-of-custody', icon: Workflow, key: 'chain' },
  { name: 'Product Insights', href: '/mvp/test-results', icon: Thermometer, key: 'insights' },
  { name: 'Cold Chain Monitoring', href: '/mvp/sensor-console', icon: FlaskConical, key: 'coldchain' },
  { name: 'Compliance', href: '/mvp/compliance', icon: Shield, key: 'compliance' },
];

const navForRole = (role) => {
  const perms = permissions(role);
  return NAV_BASE.filter((n) => (n.key === 'insights' ? perms.showProductInsights : true));
};

const getRoleColor = (role) => {
  const m = {
    supply_chain_manager: 'bg-blue-100 text-blue-700',
    sustainability_manager: 'bg-teal-100 text-teal-700',
    external_viewer: 'bg-slate-100 text-slate-700',
  };
  return m[role] || 'bg-slate-100 text-slate-700';
};

const getRoleName = (role) => {
  const m = {
    supply_chain_manager: 'Supply Chain Manager',
    sustainability_manager: 'Sustainability Manager',
    external_viewer: 'External Auditor',
  };
  return m[role] || role;
};

/**
 * Reusable sidebar contents. Used in both the persistent desktop sidebar
 * AND the mobile slide-in Sheet.
 */
const SidebarContent = ({ user, onLogout, onNavigate, compact = false }) => {
  const location = useLocation();
  const navigation = navForRole(user?.role);
  return (
    <div className="flex flex-col h-full bg-white">
      {/* Logo */}
      <div className="py-6 sm:py-8 px-4 sm:px-6 border-b border-slate-200">
        <img
          src="https://customer-assets.emergentagent.com/job_farm-to-table-135/artifacts/jfihotrx_IMG_5685.png"
          alt="PureSustain Logo"
          className="w-full h-auto"
          style={{
            maxHeight: compact ? '120px' : '160px',
            objectFit: 'contain',
            filter: 'contrast(1.35) brightness(1.15) saturate(1.2)',
            imageRendering: '-webkit-optimize-contrast',
            WebkitFontSmoothing: 'antialiased',
          }}
        />
      </div>

      {/* Navigation */}
      <nav
        className="flex-1 p-3 sm:p-4 space-y-1 overflow-y-auto"
        data-testid="sidebar-nav"
      >
        {navigation.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== '/mvp' && location.pathname.startsWith(item.href));
          return (
            <NavLink
              key={item.name}
              to={item.href}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={onNavigate}
              className={`flex items-center gap-3 px-3 sm:px-4 py-3 rounded-lg transition-all duration-200 min-h-[44px] ${
                isActive
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 active:bg-slate-100'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">{item.name}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* User Profile */}
      <div className="p-3 sm:p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="flex-shrink-0">
            <AvatarImage src="https://customer-assets.emergentagent.com/job_farm-to-table-135/artifacts/xpjs1q1r_IMG_5536.jpeg" />
            <AvatarFallback>
              {user?.name?.split(' ').map((n) => n[0]).join('') || '?'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">
              {user?.name}
            </p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <div className="mb-3">
          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user?.role)}`}
          >
            {getRoleName(user?.role)}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onLogout}
          className="w-full h-10"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>

      <OwnershipFooter variant="sidebar" />
    </div>
  );
};

const DashboardLayout = ({ children, user, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  return (
    <div
      className="min-h-screen flex bg-slate-50"
      data-testid="dashboard-layout"
    >
      {/* Desktop sidebar — visible from lg breakpoint up */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 border-r border-slate-200">
        <SidebarContent user={user} onLogout={onLogout} />
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {/* Mobile menu toggle */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="lg:hidden h-10 w-10 flex-shrink-0"
                    data-testid="mobile-menu-button"
                    aria-label="Open navigation menu"
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-72 max-w-[85vw]">
                  <SidebarContent
                    user={user}
                    onLogout={() => {
                      closeMobile();
                      onLogout();
                    }}
                    onNavigate={closeMobile}
                    compact
                  />
                </SheetContent>
              </Sheet>

              <p className="text-xs sm:text-sm font-medium text-slate-700 truncate">
                Supply Chain Portal
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-700 items-center">
                ✓ All Systems Operational
              </div>
              <div
                className="sm:hidden h-2.5 w-2.5 rounded-full bg-emerald-500"
                aria-label="Systems operational"
                title="All systems operational"
              />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 overflow-x-hidden">
          <div className="max-w-7xl mx-auto w-full">{children}</div>
        </main>

        <OwnershipFooter />
      </div>
    </div>
  );
};

export default DashboardLayout;
